import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { X, User, Lock, CreditCard, Zap, CalendarDays, BarChart2, FileText } from "lucide-react";
import { ScaleLoader } from "react-spinners";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useStripe } from "@stripe/react-stripe-js";
import Modal from "components/modal";
import PlanDetails from "sections/modal/plan-details";
import useModal from "utils/hooks/use-modal";
import { updateUser, getUserByCogId, updateCognitoUserPassword } from "api/user";
import { getSubscriptionData } from "api/subscriptions";
import { getUsageData } from "api/usage";
import { getAllStripeProducts, createCheckoutSession } from "api/stripe";
import { MEDICAL_PROFESSIONS } from "enums";
import CustomDropdownProfession from "components/dropdown-profession";
import InputFieldLabel from "components/input-field-label";
import ShimmerLoader from "components/shimmer-loader";
import { ReactComponent as EyeOpen } from "assets/svgs/eye-open.svg";
import { ReactComponent as EyeClose } from "assets/svgs/eye-close.svg";
import getFormattedDate from "utils/methods/getFormattedDate";
import capitalizeString from "utils/methods/capitalizeString";

const COMPACT_INPUT = "h-9 text-sm px-3 border border-gray-300 rounded-lg w-full bg-white text-gray-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-colors placeholder-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed";

// ─── Subscription card shell ─────────────────────────────────────────────────
function SubCard({ icon: Icon, iconBg, title, children, className = "" }) {
  return (
    <div className={`rounded-xl p-4 border flex flex-col gap-1.5 ${className}`}>
      <div className="flex items-center gap-2 mb-0.5">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon size={13} className="text-white" />
        </span>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
      </div>
      {children}
    </div>
  );
}

const ProfileModal = ({ onClose }) => {
  const queryClient = useQueryClient();
  const stripe = useStripe();

  const navigate = useNavigate();
  const { openModal: openPlanModal, closeModal: closePlanModal, isOpen: isPlanOpen } = useModal(false);
  const [planLoading, setPlanLoading] = useState(null);

  const {
    control, register, handleSubmit, formState: { errors },
  } = useForm();

  const {
    reset: resetPw, watch,
    register: registerPw, handleSubmit: handleSubmitPw,
    formState: { errors: pwErrors },
  } = useForm();
  const password = watch("password");

  const [profileLoading, setProfileLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [hideOldPw, setHideOldPw] = useState(false);
  const [hideNewPw, setHideNewPw] = useState(false);
  const [hideConfirmPw, setHideConfirmPw] = useState(false);

  const { data, isLoading: profileDataLoading } = useQuery("userProfile", getUserByCogId);
  const { data: subscriptionData, isLoading: subLoading, isError: subError } = useQuery(
    "userSubscription", getSubscriptionData, { retry: false }
  );
  const { data: usageData } = useQuery("userUsage", getUsageData, { retry: false });
  const { data: stripeProducts } = useQuery("stripe-products", getAllStripeProducts, { retry: false });

  const updateUserMutation = useMutation(updateUser, {
    onSuccess: () => {
      toast.success("Profile saved");
      queryClient.refetchQueries("userProfile");
    },
    onError: () => toast.error("Error updating profile"),
    onSettled: () => setProfileLoading(false),
  });

  const updatePasswordMutation = useMutation(updateCognitoUserPassword, {
    onSuccess: () => { toast.success("Password updated"); resetPw(); },
    onError: () => toast.error("Failed updating password"),
    onSettled: () => setPwLoading(false),
  });

  const handleCheckout = async (event, priceId) => {
    event.preventDefault();
    setPlanLoading(priceId);
    try {
      const response = await createCheckoutSession({ priceId });
      const result = await stripe.redirectToCheckout({ sessionId: response.id });
      if (result.error) toast.error(result.error.message || "Payment failed", { position: "top-right" });
    } catch (error) {
      toast.error(error?.response?.data?.error || "Error processing payment!", { position: "top-right" });
    } finally {
      setPlanLoading(null);
    }
  };

  const onProfileSubmit = (formData) => {
    setProfileLoading(true);
    updateUserMutation.mutate({
      firstName: formData.firstName || data?.firstName || "",
      lastName: formData.lastName || data?.lastName || "",
      profession:
        MEDICAL_PROFESSIONS.find((el) => el.id === formData.profession)?.name ||
        data?.profession || "",
      commonTemplate: data?.commonTemplate || "",
      commonConditions: data?.commonConditions || "",
    });
  };

  const onPasswordSubmit = (formData) => {
    setPwLoading(true);
    updatePasswordMutation.mutate({ newPassword: formData.password, oldPassword: formData.oldPassword });
  };

  // ── Subscription helpers ─────────────────────────────────────────────────
  const hasPaidSub = !subError && subscriptionData;
  const isActive = subscriptionData?.status === "active" || subscriptionData?.status === "trialing";
  const isFreeTrial = usageData?.isFreeTrial;
  const sessionLimit = Number(process.env.REACT_APP_TRIAL_USER_LIMIT_SESSIONS) || 60;

  const getPriceInfo = () => {
    if (!stripeProducts || !subscriptionData?.planDuration?.stripePriceId) return null;
    return Object.values(stripeProducts).flat().find(p => p.id === subscriptionData.planDuration.stripePriceId);
  };

  const displayPrice = () => {
    const p = getPriceInfo();
    if (!p) return null;
    return `${p.currency.toUpperCase()} ${p.unit_amount / 100} / ${p.recurring.interval}`;
  };

  const getRenewalLabel = () => {
    if (subscriptionData?.canceledAt) return "Plan ends";
    if (subscriptionData?.status === "trialing") return "Trial ends";
    return "Next billing";
  };

  const sessionPct = isFreeTrial
    ? Math.min(100, Math.round(((usageData?.sessions || 0) / sessionLimit) * 100))
    : null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">My Profile</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">
          {profileDataLoading ? (
            <div className="space-y-3">
              <ShimmerLoader className="h-9 w-full rounded-lg" />
              <ShimmerLoader className="h-9 w-full rounded-lg" />
              <ShimmerLoader className="h-9 w-3/4 rounded-lg" />
            </div>
          ) : (
            <>
              {/* ── Personal Info + Change Password ─────────────────── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                {/* Personal Info */}
                <div className="bg-gray-50 rounded-xl p-5 flex flex-col">
                  <div className="flex items-center gap-2 mb-4">
                    <User size={15} className="text-brand-purple" />
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Personal Info</h3>
                  </div>
                  <form onSubmit={handleSubmit(onProfileSubmit)} className="flex flex-col flex-1">
                    <div className="space-y-3 flex-1">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <InputFieldLabel label="First Name" isRequired className="mb-1 text-xs" />
                          <input
                            {...register("firstName", { required: "Required" })}
                            defaultValue={data?.firstName || ""}
                            placeholder="First name"
                            className={COMPACT_INPUT}
                          />
                          {errors.firstName && <span className="text-red text-xs">{errors.firstName.message}</span>}
                        </div>
                        <div>
                          <InputFieldLabel label="Last Name" isRequired className="mb-1 text-xs" />
                          <input
                            {...register("lastName", { required: "Required" })}
                            defaultValue={data?.lastName || ""}
                            placeholder="Last name"
                            className={COMPACT_INPUT}
                          />
                          {errors.lastName && <span className="text-red text-xs">{errors.lastName.message}</span>}
                        </div>
                      </div>

                      <div>
                        <InputFieldLabel label="Email" className="mb-1 text-xs" />
                        <input readOnly disabled defaultValue={data?.email || ""} className={COMPACT_INPUT} />
                      </div>

                      <div>
                        <InputFieldLabel label="Profession" isRequired className="mb-1 text-xs" />
                        <Controller
                          name="profession"
                          control={control}
                          rules={{ required: "Profession is required" }}
                          defaultValue={
                            data?.profession
                              ? (MEDICAL_PROFESSIONS.find((el) => el.name === data.profession)?.id ?? null)
                              : null
                          }
                          render={({ field: { onChange, value } }) => (
                            <CustomDropdownProfession
                              compact
                              value={value}
                              className="w-full"
                              onSelect={onChange}
                              options={MEDICAL_PROFESSIONS}
                              placeholder={
                                MEDICAL_PROFESSIONS.find((el) => el.id === value)?.name || "Choose profession"
                              }
                            />
                          )}
                        />
                        {errors.profession && <span className="text-red text-xs">{errors.profession.message}</span>}
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 mt-auto">
                      <button
                        type="submit"
                        disabled={profileLoading}
                        className="bg-green border border-gray-600 rounded-lg px-4 py-1.5 text-sm relative disabled:cursor-not-allowed hover:bg-green/90 transition-colors"
                      >
                        <span className={profileLoading ? "opacity-0" : "opacity-100"}>Save</span>
                        {profileLoading && (
                          <ScaleLoader loading className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" height={12} />
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Change Password */}
                <div className="bg-gray-50 rounded-xl p-5 flex flex-col">
                  <div className="flex items-center gap-2 mb-4">
                    <Lock size={15} className="text-brand-purple" />
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Change Password</h3>
                  </div>
                  <form onSubmit={handleSubmitPw(onPasswordSubmit)} className="flex flex-col flex-1">
                    <div className="space-y-3 flex-1">
                      {[
                        {
                          id: "oldPassword", label: "Current Password", visible: hideOldPw,
                          toggle: () => setHideOldPw(s => !s),
                          reg: registerPw("oldPassword", {
                            required: "Current password cannot be empty",
                            pattern: { value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]{8,}$/, message: "8+ chars with uppercase, lowercase, number, and special char." },
                          }),
                          error: pwErrors.oldPassword,
                        },
                        {
                          id: "password", label: "New Password", visible: hideNewPw,
                          toggle: () => setHideNewPw(s => !s),
                          reg: registerPw("password", {
                            required: "Password cannot be empty",
                            pattern: { value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]{8,}$/, message: "8+ chars with uppercase, lowercase, number, and special char." },
                          }),
                          error: pwErrors.password,
                        },
                        {
                          id: "confirmPassword", label: "Confirm New Password", visible: hideConfirmPw,
                          toggle: () => setHideConfirmPw(s => !s),
                          reg: registerPw("confirmPassword", {
                            required: "Please confirm your password",
                            validate: v => v === password || "Passwords do not match",
                          }),
                          error: pwErrors.confirmPassword,
                        },
                      ].map(({ id, label, visible, toggle, reg, error }) => (
                        <div key={id}>
                          <InputFieldLabel label={label} className="mb-1 text-xs" />
                          <div className="relative">
                            <input
                              {...reg}
                              type={visible ? "text" : "password"}
                              placeholder={label}
                              className={`${COMPACT_INPUT} pr-10`}
                            />
                            <button type="button" onClick={toggle} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer">
                              {!visible ? <EyeOpen /> : <EyeClose />}
                            </button>
                          </div>
                          {error && <span className="text-red text-xs">{error.message}</span>}
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end pt-4 mt-auto">
                      <button
                        type="submit"
                        disabled={pwLoading}
                        className="bg-green border border-gray-600 rounded-lg px-4 py-1.5 text-sm relative disabled:cursor-not-allowed hover:bg-green/90 transition-colors"
                      >
                        <span className={pwLoading ? "opacity-0" : "opacity-100"}>Update Password</span>
                        {pwLoading && (
                          <ScaleLoader loading className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" height={12} />
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* ── Subscription ─────────────────────────────────────── */}
              <div className="border-t border-gray-100 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => { onClose(); navigate("/subscription"); }}
                    className="flex items-center gap-2 group"
                  >
                    <CreditCard size={15} className="text-brand-purple" />
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide border-b-2 border-gray-700 group-hover:border-brand-purple group-hover:text-brand-purple transition-colors">
                      Subscription
                    </h3>
                  </button>
                  <button
                    onClick={openPlanModal}
                    className={`shadow-sm hover:shadow-md rounded-lg py-1.5 px-4 text-sm font-semibold transition-all duration-200 ${
                      isActive
                        ? "bg-primary border border-gray-300"
                        : "bg-gradient-to-r from-pink-500 to-purple-600 text-white border-gray-300 hover:from-pink-600 hover:to-purple-700"
                    }`}
                  >
                    {isActive ? "Manage Plan" : "Upgrade Plan"}
                  </button>
                </div>

                {subLoading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[1, 2, 3].map(i => <ShimmerLoader key={i} className="h-24 rounded-xl" />)}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

                    {/* ── Plan card ─────────────────────────────────── */}
                    {hasPaidSub ? (
                      <SubCard
                        icon={Zap}
                        iconBg="bg-violet-500"
                        title="Current Plan"
                        className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200"
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-bold text-gray-900 leading-tight">
                            {subscriptionData.planDuration?.plan?.name ?? "Pro"}
                          </p>
                          <span
                            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                              isActive
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {isActive ? "Active" : capitalizeString(subscriptionData.status || "")}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 leading-tight">
                          {getPriceInfo()?.recurring?.interval === "month" ? "Monthly" : "Annual"}
                          {displayPrice() ? ` · ${displayPrice()}` : ""}
                        </p>
                      </SubCard>
                    ) : (
                      <SubCard
                        icon={Zap}
                        iconBg="bg-teal-500"
                        title="Current Plan"
                        className="bg-gradient-to-br from-cyan-50 to-teal-50 border-teal-200"
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-bold text-gray-900">Basic</p>
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            Active
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">Free forever</p>
                        <p className="text-xs text-gray-400">{sessionLimit} sessions / month</p>
                      </SubCard>
                    )}

                    {/* ── Patient Sessions card ─────────────────────── */}
                    <SubCard
                      icon={BarChart2}
                      iconBg="bg-teal-500"
                      title="Patient Sessions"
                      className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200"
                    >
                      <p className="text-sm font-bold text-gray-900">
                        {usageData?.sessions ?? 0}
                        {isFreeTrial ? <span className="font-normal text-gray-500"> of {sessionLimit}</span> : ""}
                      </p>
                      {isFreeTrial && sessionPct !== null && (
                        <div className="mt-1">
                          <div className="h-1.5 w-full bg-blue-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-400 rounded-full transition-all duration-500"
                              style={{ width: `${sessionPct}%` }}
                            />
                          </div>
                        </div>
                      )}
                      {usageData?.nextResetDate && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          Resets {getFormattedDate(usageData.nextResetDate)}
                        </p>
                      )}
                      {hasPaidSub && subscriptionData?.endDate && (
                        <div className="mt-1">
                          <p className="text-[11px] text-gray-400">{getRenewalLabel()} {getFormattedDate(subscriptionData.endDate)}</p>
                        </div>
                      )}
                    </SubCard>

                    {/* ── Notes Created card ────────────────────────── */}
                    <SubCard
                      icon={FileText}
                      iconBg="bg-teal-500"
                      title="Notes Created"
                      className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200"
                    >
                      <p className="text-sm font-bold text-gray-900">{usageData?.notes ?? 0}</p>
                      <p className="text-xs text-gray-400">Total notes generated</p>
                    </SubCard>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Plan selection modal */}
      <Modal isOpen={isPlanOpen} closeModal={closePlanModal}>
        <PlanDetails
          loading={planLoading}
          products={stripeProducts}
          userUsageData={usageData}
          subscriptionData={subscriptionData}
          handleClickGetStarted={handleCheckout}
        />
      </Modal>
    </div>
  );
};

export default ProfileModal;
